import React, { useState } from 'react';
import { Input, InputNumber, Button, Modal, Spin, DatePicker } from 'antd';
import { useWorkScheduleForm } from './hooks/useWorkScheduleForm';
import dayjs from 'dayjs';

const P910GForm: React.FC = () => {
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    code, setCode,
    effectiveDate, setEffectiveDate,
    weekdayStart, setWeekdayStart,
    weekdayEnd, setWeekdayEnd,
    saturdayStart, setSaturdayStart,
    saturdayEnd, setSaturdayEnd,
    lunchStart, setLunchStart,
    lunchEnd, setLunchEnd,
    flexMinutes, setFlexMinutes,
    dailyHours, setDailyHours,
    remark, setRemark,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
  } = useWorkScheduleForm();

  const [searchCode, setSearchCode] = useState('');

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除班別 ${current.f90g_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleQuickSearch = () => {
    if (searchCode.trim()) quickSearch(searchCode.trim());
  };

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  return (
    <Spin spinning={loading}>
      <div className="max-w-3xl mx-auto p-6">
        <h2 className="text-xl font-bold mb-4">
          上班時間定義 (pw910gf1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        {/* Quick Search */}
        <div className="flex items-center gap-2 mb-4">
          <Input
            placeholder="輸入班別代號查詢"
            value={searchCode}
            onChange={e => setSearchCode(e.target.value)}
            onPressEnter={handleQuickSearch}
            disabled={isEditing}
            style={{ width: 180 }}
            maxLength={4}
          />
          <Button onClick={handleQuickSearch} disabled={isEditing}>查詢</Button>
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-1 mb-4">
          <Button size="small" onClick={navFirst} disabled={isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || records.length === 0}>&gt;|</Button>
        </div>

        {/* Form Fields */}
        <div className="space-y-4 mb-6">
          {/* Row 1: Code + Effective Date */}
          <div className="flex gap-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">01 班別代號</label>
              <Input value={code} onChange={e => setCode(e.target.value)} maxLength={4}
                disabled={mode !== 'add'} style={{ width: 120 }} placeholder="最多4碼" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">02 開始日期</label>
              <DatePicker
                value={effectiveDate ? dayjs(effectiveDate) : null}
                onChange={(d) => setEffectiveDate(d ? d.startOf('day').toISOString() : null)}
                disabled={!isEditing}
                style={{ width: 160 }}
              />
            </div>
          </div>

          {/* Row 2: Weekday / Saturday headers + times */}
          <div>
            <div className="flex gap-8 mb-1">
              <span className="text-sm text-gray-500" style={{ width: 120 }}></span>
              <span className="text-sm font-medium text-gray-600" style={{ width: 120 }}>週一~五</span>
              <span className="text-sm font-medium text-gray-600" style={{ width: 120 }}>週六</span>
            </div>
            <div className="flex gap-8 mb-3">
              <label className="text-sm font-medium text-gray-700" style={{ width: 120, lineHeight: '32px' }}>03 上班時間</label>
              <Input value={weekdayStart} onChange={e => setWeekdayStart(e.target.value)} maxLength={5}
                disabled={!isEditing} style={{ width: 120 }} placeholder="HHmm" />
              <Input value={saturdayStart} onChange={e => setSaturdayStart(e.target.value)} maxLength={5}
                disabled={!isEditing} style={{ width: 120 }} placeholder="HHmm" />
            </div>
            <div className="flex gap-8">
              <label className="text-sm font-medium text-gray-700" style={{ width: 120, lineHeight: '32px' }}>04 下班時間</label>
              <Input value={weekdayEnd} onChange={e => setWeekdayEnd(e.target.value)} maxLength={5}
                disabled={!isEditing} style={{ width: 120 }} placeholder="HHmm" />
              <Input value={saturdayEnd} onChange={e => setSaturdayEnd(e.target.value)} maxLength={5}
                disabled={!isEditing} style={{ width: 120 }} placeholder="HHmm" />
            </div>
          </div>

          {/* Row 3: Lunch break */}
          <div className="flex gap-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">05 午休開始</label>
              <Input value={lunchStart} onChange={e => setLunchStart(e.target.value)} maxLength={5}
                disabled={!isEditing} style={{ width: 120 }} placeholder="HHmm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">06 午休停止</label>
              <Input value={lunchEnd} onChange={e => setLunchEnd(e.target.value)} maxLength={5}
                disabled={!isEditing} style={{ width: 120 }} placeholder="HHmm" />
            </div>
          </div>

          {/* Row 4: Flex time */}
          <div className="flex gap-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">07 彈性上班時間</label>
              <div className="flex items-center gap-2">
                <InputNumber value={flexMinutes} onChange={v => setFlexMinutes(v ?? 0)} min={0} max={999}
                  disabled={!isEditing} style={{ width: 100 }} />
                <span className="text-sm text-gray-500">分鐘</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">08 上班時數</label>
              <div className="flex items-center gap-2">
                <InputNumber value={dailyHours} onChange={v => setDailyHours(v ?? 0)} min={0} max={24} step={0.5}
                  disabled={!isEditing} style={{ width: 100 }} />
                <span className="text-sm text-gray-500">小時</span>
              </div>
            </div>
          </div>

          {/* Row 5: Remark */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">09 備註</label>
            <Input value={remark} onChange={e => setRemark(e.target.value)} maxLength={60}
              disabled={!isEditing} style={{ width: 480 }} placeholder="最多60字" />
          </div>
        </div>

        {/* Audit Fields */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-6 space-y-1">
            <div>建立者: {current.f90g_91} | 建立日期: {current.f90g_92 || '-'}</div>
            <div>最後修改者: {current.f90g_93}</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={startAdd}>新增</Button>
              <Button onClick={startEdit} disabled={!current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default P910GForm;
